import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Container,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import html2pdf from 'html2pdf.js';

// Icons
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentIcon from "@mui/icons-material/Payment";
import EmailIcon from "@mui/icons-material/Email";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";

// Services
import * as saleOrderService from "../services/saleOrderService";
import { generateAndPreviewSaleOrderPDF } from "../services/saleOrderPdfService";
import { PAYMENT_STATUS } from '../../../constants/paymentStatus';

// Components
import SaleOrderStatusChip from "../components/SaleOrderStatusChip";
import PaymentStatusChip from "../components/PaymentStatusChip";
import ConvertToInvoiceDialog from "../components/ConvertToInvoiceDialog";
import RecordPaymentDialog from "../components/RecordPaymentDialog";
import ConfirmationDialog from "../../../components/ConfirmationDialog";

// Hooks
import useStoreInfo from '../../../hooks/useCompanyInfo';

function SaleOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { storeInfo } = useStoreInfo();
  const language = useSelector((state) => state.settings?.language) || 'en';
  
  // State
  const [saleOrder, setSaleOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  // Load sale order
  useEffect(() => {
    loadSaleOrder();
    loadPayments();
  }, [id]);

  const loadSaleOrder = async () => {
    setLoading(true);
    try {
      const response = await saleOrderService.getSaleOrderById(id);
      const data = response?.payload || response?.data || response;
      setSaleOrder(data);
    } catch (error) {
      console.error('Error loading sale order:', error);
      toast.error(t('failed_to_load_sale_order'));
      navigate('/sales/sale-orders');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const response = await saleOrderService.getSaleOrderPayments(id);
      const data = response?.payload || response?.data || response || [];
      setPayments(data);
    } catch (error) {
      console.error('Error loading payments:', error);
      // Don't show error toast for payments as it's not critical
    }
  };

  // Handle convert to invoice
  const handleConvertToInvoice = async () => {
    try {
      const response = await saleOrderService.convertSaleOrderToInvoice(id);
      const invoice = response?.payload || response?.data || response;
      toast.success(t('sale_order_converted_to_invoice'));
      // Navigate to invoice detail page
      if (invoice?.Id) {
        navigate(`/invoices/${invoice.Id}`);
      } else {
        navigate('/invoices');
      }
    } catch (error) {
      console.error('Error converting to invoice:', error);
      toast.error(t('failed_to_convert_to_invoice'));
    }
  };

  // Handle record payment
  const handleRecordPayment = async (paymentData) => {
    try {
      await saleOrderService.recordSaleOrderPayment(id, paymentData);
      toast.success(t('payment_recorded'));
      loadSaleOrder();
      loadPayments();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(t('failed_to_record_payment'));
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await saleOrderService.deleteSaleOrder(id);
      toast.success(t('sale_order_deleted'));
      navigate('/sales/sale-orders');
    } catch (error) {
      console.error('Error deleting sale order:', error);
      toast.error(t('failed_to_delete_sale_order'));
    }
  };

  // Handle close sale order
  const handleCloseSaleOrder = async () => {
    try {
      await saleOrderService.closeSaleOrder(id);
      toast.success(t('sale_order_closed_successfully'));
      loadSaleOrder();
    } catch (error) {
      console.error('Error closing sale order:', error);
      toast.error(t('failed_to_close_sale_order'));
    }
  };

  // Handle download PDF
  const handleDownloadPdf = async () => {
    try {
      await generateAndPreviewSaleOrderPDF(saleOrder, storeInfo, language, t);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('failed_to_generate_pdf'));
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (!saleOrder) {
    return null;
  }

  const customer = saleOrder.Customer || {};
  const lineItems = saleOrder.LineItems || [];
  const totalExcl = lineItems.reduce((sum, item) => sum + Number(item.TotalExcl || 0), 0);
  const totalVAT = lineItems.reduce((sum, item) => sum + Number(item.VATAmount || 0), 0);
  const grandTotal = lineItems.reduce((sum, item) => sum + Number(item.TotalIncl || 0), 0);
  const paidAmount = Number(saleOrder.PaidAmount || 0);
  const dueAmount = grandTotal - paidAmount;
  const isDraft = saleOrder.Status?.toLowerCase() === 'draft';
  const isClosed = saleOrder.Status?.toLowerCase() === 'closed';
  const canRecordPayment = isClosed && saleOrder.PaymentStatus !== PAYMENT_STATUS.PAID;
  const canConvertToInvoice = isClosed;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShoppingCartIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {saleOrder.SaleOrderNumber}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <SaleOrderStatusChip status={saleOrder.Status} sx={{ bgcolor: 'white', color: '#667eea' }} />
                <PaymentStatusChip paymentStatus={saleOrder.PaymentStatus} sx={{ bgcolor: 'white' }} />
              </Box>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/sales/sale-orders')}
            sx={{
              bgcolor: 'white',
              color: '#667eea',
              fontWeight: 700,
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            {t('back')}
          </Button>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {isDraft && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/sales/sale-orders/add?id=${id}`)}
              >
                {t('continue')}
              </Button>
              <Button
                variant="contained"
                startIcon={<ShoppingCartIcon />}
                onClick={() => setCloseDialogOpen(true)}
                sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
              >
                {t('close_sale_order')}
              </Button>
            </>
          )}
          {isClosed && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/sales/sale-orders/add?id=${id}`)}
              sx={{ bgcolor: '#2196f3', '&:hover': { bgcolor: '#1976d2' } }}
            >
              {t('reopen_sale_order')}
            </Button>
          )}
          {canRecordPayment && (
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => setPaymentDialogOpen(true)}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
            >
              {t('record_payment')}
            </Button>
          )}
          {canConvertToInvoice && (
            <Button
              variant="contained"
              startIcon={<ReceiptLongIcon />}
              onClick={() => setConvertDialogOpen(true)}
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
            >
              {t('convert_to_invoice')}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownloadPdf}
          >
            {t('download_pdf')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            {t('delete')}
          </Button>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Customer Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('customer_information')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <BusinessIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('company_name')}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {customer.CompanyName || customer.Name || t('unknown')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <EmailIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('email')}
                      </Typography>
                      <Typography variant="body2">
                        {customer.Email || t('no_email')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PhoneIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('phone')}
                      </Typography>
                      <Typography variant="body2">
                        {customer.PhoneNumber || customer.Phone || t('no_phone')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <LocationOnIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('address')}
                      </Typography>
                      <Typography variant="body2">
                        {[customer.Street, customer.City, customer.PostCode, customer.Country]
                          .filter(Boolean)
                          .join(', ') || t('no_address')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('line_items')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('product')}</TableCell>
                      <TableCell align="center">{t('quantity')}</TableCell>
                      <TableCell align="right">{t('unit_price')}</TableCell>
                      <TableCell align="right">{t('total_excl_vat')}</TableCell>
                      <TableCell align="right">{t('vat')}</TableCell>
                      <TableCell align="right">{t('total_incl_vat')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.Name || item.ProductName}</TableCell>
                        <TableCell align="center">{item.Quantity}</TableCell>
                        <TableCell align="right">€{Number(item.UnitPrice || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">€{Number(item.TotalExcl || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">€{Number(item.VATAmount || 0).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          €{Number(item.TotalIncl || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>

          {/* Payment History */}
          {payments.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t('payment_history')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('date')}</TableCell>
                        <TableCell>{t('payment_method')}</TableCell>
                        <TableCell align="right">{t('amount')}</TableCell>
                        <TableCell>{t('notes')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment, index) => (
                        <TableRow key={index}>
                          <TableCell>{formatDate(payment.PaymentDate)}</TableCell>
                          <TableCell>{payment.PaymentMethod?.Name || t('unknown')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            €{Number(payment.Amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>{payment.Notes || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('summary')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('date')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDate(saleOrder.SaleOrderDate || saleOrder.CreatedDate)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('items')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {lineItems.length}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('subtotal')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    €{totalExcl.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('vat')} ({saleOrder.VATPercentage || 21}%):
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    €{totalVAT.toFixed(2)}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight={700}>
                    {t('total')}:
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary">
                    €{grandTotal.toFixed(2)}
                  </Typography>
                </Box>
                
                {paidAmount > 0 && (
                  <>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="success.main">
                        {t('paid')}:
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        €{paidAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="error.main">
                        {t('due')}:
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="error.main">
                        €{dueAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {saleOrder.CustomText && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {t('notes')}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {saleOrder.CustomText}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <ConvertToInvoiceDialog
        open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        onConfirm={handleConvertToInvoice}
        saleOrder={saleOrder}
      />

      <RecordPaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        onConfirm={handleRecordPayment}
        saleOrder={saleOrder}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('confirm_delete')}
        message={t('confirm_delete_sale_order_message', { number: saleOrder.SaleOrderNumber })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        confirmText={t('delete')}
        cancelText={t('cancel')}
        variant="danger"
      />

      <ConfirmationDialog
        open={closeDialogOpen}
        title={t('close_sale_order')}
        message={t('confirm_close_sale_order_message')}
        onConfirm={handleCloseSaleOrder}
        onCancel={() => setCloseDialogOpen(false)}
        confirmText={t('close')}
        cancelText={t('cancel')}
        variant="success"
      />
    </Container>
  );
}

export default SaleOrderDetailPage;
